<template>
  <div style="position: relative;">
    <button
      class="btn-tertiary"
      style="padding: 0.25rem; display: flex; align-items: center; justify-content: center;"
      aria-label="User menu"
      @click="open = !open"
    >
      <span class="user-avatar">{{ initial }}</span>
    </button>

    <div
      v-if="open"
      v-click-outside="() => open = false"
      class="user-menu"
    >
      <div class="user-menu-identity">
        <p style="font-family: 'Manrope', sans-serif; font-weight: 800; font-size: 0.875rem; color: var(--color-on-surface);">
          {{ user?.name || "My Account" }}
        </p>
        <p
          class="text-body-sm"
          style="color: var(--color-on-surface-variant); margin-top: 0.125rem;"
        >
          {{ user?.email_address }}
        </p>
      </div>

      <div style="padding: 0.375rem 0;">
        <router-link
          v-for="link in links"
          :key="link.to"
          :to="link.to"
          class="user-menu-item"
          @click="open = false"
        >
          <span
            class="material-symbols-rounded"
            style="font-size: 1.125rem; color: var(--color-on-surface-variant);"
          >
            {{ link.icon }}
          </span>
          <span class="text-body-md">{{ link.label }}</span>
        </router-link>
      </div>

      <div class="user-menu-footer">
        <button
          class="user-menu-item"
          style="color: var(--color-error); width: 100%; text-align: left; background: none; border: none;"
          @click="onSignOut"
        >
          <span
            class="material-symbols-rounded"
            style="font-size: 1.125rem;"
          >logout</span>
          <span class="text-body-md">Sign out</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, type DirectiveBinding } from "vue"
import { useRouter } from "vue-router"
import { useAuthStore } from "@/stores/auth"
import { apiClient } from "@/api/client"

const auth = useAuthStore()
const router = useRouter()
const open = ref(false)
const user = computed(() => auth.user)
const initial = computed(() => {
  const name = user.value?.name || user.value?.email_address || "?"
  return name[0].toUpperCase()
})

const links = [
  { to: "/profile",     label: "Profile",       icon: "manage_accounts" },
  { to: "/preferences", label: "AI Context",    icon: "psychology" },
  { to: "/time-slots",  label: "Time Slots",    icon: "schedule" },
  { to: "/reports",     label: "Reports",       icon: "bar_chart_4_bars" },
  { to: "/guide",       label: "Feature Guide", icon: "help_outline" },
]

async function onSignOut() {
  try { await apiClient.delete("/auth/logout") } catch { /* ignore */ }
  auth.clear()
  open.value = false
  router.replace("/login")
}

const vClickOutside = {
  mounted(el: HTMLElement, binding: DirectiveBinding<() => void>) {
    const handler = (e: Event) => {
      if (!el.contains(e.target as Node)) binding.value()
    }
    ;(el as HTMLElement & { _clickOutside?: typeof handler })._clickOutside = handler
    setTimeout(() => document.addEventListener("click", handler), 0)
  },
  unmounted(el: HTMLElement) {
    const handler = (el as HTMLElement & { _clickOutside?: (e: Event) => void })._clickOutside
    if (handler) document.removeEventListener("click", handler)
  },
}
</script>

<style scoped>
.user-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 9999px;
  background: var(--color-primary-container);
  color: var(--color-on-primary-container);
  font-family: 'Manrope', sans-serif;
  font-weight: 800;
  font-size: 0.8125rem;
}
.user-menu {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  min-width: 13rem;
  z-index: 500;
  background: var(--color-surface);
  border-radius: 1rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}
.user-menu-identity {
  padding: 0.875rem 1rem 0.75rem;
  border-bottom: 1px solid var(--color-surface-variant);
}
.user-menu-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 1rem;
  text-decoration: none;
  color: var(--color-on-surface);
  cursor: pointer;
}
.user-menu-item:hover {
  background: var(--color-surface-container-low);
}
.user-menu-footer {
  padding: 0.375rem 0;
  border-top: 1px solid var(--color-surface-variant);
}
</style>
